"use client";

import { useTranslations } from "next-intl";

/** SPI 表面处理表格：5 列。第一列 SPI Grade 为分组+子级（首行显示 "SPI A" + "A-1"，其余行仅子级）用 rowSpan 合并分组 */
const ROWS: {
	groupKey: string;
	gradeKey: string;
	finishKey: string;
	methodKey: string;
	raKey: string;
	appKey: string;
}[] = [
	{
		groupKey: "groupA",
		gradeKey: "a1",
		finishKey: "a1Finish",
		methodKey: "a1Method",
		raKey: "a1Ra",
		appKey: "a1App",
	},
	{
		groupKey: "",
		gradeKey: "a2",
		finishKey: "a2Finish",
		methodKey: "a2Method",
		raKey: "a2Ra",
		appKey: "a2App",
	},
	{
		groupKey: "",
		gradeKey: "a3",
		finishKey: "a3Finish",
		methodKey: "a3Method",
		raKey: "a3Ra",
		appKey: "a3App",
	},
	{
		groupKey: "groupB",
		gradeKey: "b1",
		finishKey: "b1Finish",
		methodKey: "b1Method",
		raKey: "b1Ra",
		appKey: "b1App",
	},
	{
		groupKey: "",
		gradeKey: "b2",
		finishKey: "b2Finish",
		methodKey: "b2Method",
		raKey: "b2Ra",
		appKey: "b2App",
	},
	{
		groupKey: "",
		gradeKey: "b3",
		finishKey: "b3Finish",
		methodKey: "b3Method",
		raKey: "b3Ra",
		appKey: "b3App",
	},
	{
		groupKey: "groupC",
		gradeKey: "c1",
		finishKey: "c1Finish",
		methodKey: "c1Method",
		raKey: "c1Ra",
		appKey: "c1App",
	},
	{
		groupKey: "",
		gradeKey: "c2",
		finishKey: "c2Finish",
		methodKey: "c2Method",
		raKey: "c2Ra",
		appKey: "c2App",
	},
	{
		groupKey: "",
		gradeKey: "c3",
		finishKey: "c3Finish",
		methodKey: "c3Method",
		raKey: "c3Ra",
		appKey: "c3App",
	},
	{
		groupKey: "groupD",
		gradeKey: "d1",
		finishKey: "d1Finish",
		methodKey: "d1Method",
		raKey: "d1Ra",
		appKey: "d1App",
	},
	{
		groupKey: "",
		gradeKey: "d2",
		finishKey: "d2Finish",
		methodKey: "d2Method",
		raKey: "d2Ra",
		appKey: "d2App",
	},
	{
		groupKey: "",
		gradeKey: "d3",
		finishKey: "d3Finish",
		methodKey: "d3Method",
		raKey: "d3Ra",
		appKey: "d3App",
	},
];

const GROUP_ROW_SPAN: Record<string, number> = { groupA: 3, groupB: 3, groupC: 3, groupD: 3 };

export interface InjectionMoldSurfaceFinishesProps {
	translationNamespace?: string;
}

export default function InjectionMoldSurfaceFinishes({
	translationNamespace = "injectionMoldSurfaceFinishes",
}: InjectionMoldSurfaceFinishesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-[#FCFFF4] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>
				</div>

				<div className="overflow-x-auto">
					<table className="w-full min-w-[720px] border-collapse">
						<thead>
							<tr className="bg-themePrimary400 text-white">
								<th
									className="border border-themePrimary400/80 px-4 py-3 text-left text-sm font-bold sm:px-5 sm:py-4 sm:text-base"
									colSpan={2}
								>
									{t("colGrade")}
								</th>
								<th className="border border-themePrimary400/80 px-4 py-3 text-left text-sm font-bold sm:px-5 sm:py-4 sm:text-base">
									{t("colFinish")}
								</th>
								<th className="border border-themePrimary400/80 px-4 py-3 text-left text-sm font-bold sm:px-5 sm:py-4 sm:text-base">
									{t("colMethod")}
								</th>
								<th className="border border-themePrimary400/80 px-4 py-3 text-left text-sm font-bold sm:px-5 sm:py-4 sm:text-base">
									{t("colRa")}
								</th>
								<th className="border border-themePrimary400/80 px-4 py-3 text-left text-sm font-bold sm:px-5 sm:py-4 sm:text-base">
									{t("colApplication")}
								</th>
							</tr>
						</thead>
						<tbody>
							{ROWS.map((row, index) => (
								<tr key={`${row.gradeKey}-${index}`}>
									{row.groupKey ? (
										<td
											className="border border-gray-200 px-4 py-3 align-middle text-sm font-semibold text-black sm:px-5 sm:py-4 sm:text-base lg:w-[90px]"
											rowSpan={GROUP_ROW_SPAN[row.groupKey]}
										>
											{t(row.groupKey)}
										</td>
									) : null}
									<td className="border border-gray-200 px-4 py-3 text-sm text-gray-900 sm:px-5 sm:py-4 sm:text-base lg:w-[70px]">
										{t(row.gradeKey)}
									</td>
									<td className="border border-gray-200 px-4 py-3 text-sm text-black sm:px-5 sm:py-4 sm:text-base">
										{t(row.finishKey)}
									</td>
									<td className="border border-gray-200 px-4 py-3 text-sm text-black sm:px-5 sm:py-4 sm:text-base">
										{t(row.methodKey)}
									</td>
									<td className="border border-gray-200 px-4 py-3 text-sm text-black sm:px-5 sm:py-4 sm:text-base">
										{t(row.raKey)}
									</td>
									<td className="border border-gray-200 px-4 py-3 text-sm text-black sm:px-5 sm:py-4 sm:text-base">
										{t(row.appKey)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
